import {Box, Typography} from "@mui/material";
import {
  Restaurant as NutritionIcon,
  FitnessCenter as WeightIcon,
} from "@mui/icons-material";
import ClientBlocks from "./ClientBlocks";
import {useNavigate} from "react-router-dom";
import {useEffect, useState} from "react";
import {getClientWeights} from "../services/TraineeService";
import {listMenus} from "../services/NutritionV2Service";
import {getErrorMessage} from "../enums/ErrorMessages";
import {useMessageContext} from "../contexts/MessageContext";
import ClientBodyMeasurements from "./ClientBodyMeasurements";
import ChartCard from "./client/ChartCard";
import NutritionDonut from "./client/NutritionDonut";
import MetricsChart from "./client/MetricsChart";
import {colors} from "./ui/colors";
import {useNutritionDisplayMode} from "../contexts/NutritionDisplayContext";
import {POINTS, energyFigure, energyLabel, macroFigure, pointsFromMacro} from "./nutrition/v2/ui/nutritionUnits";

const ClientPageMainSection = ({userId}) => {
  let navigate = useNavigate();
  const {notificationHandler} = useMessageContext();
  const mode = useNutritionDisplayMode();
  const points = mode === POINTS;
  // Points mode: the energy as kcal ÷ 100 and each macro as its own points, in place of the grams.
  const energyNum = (kcal) => (points ? energyFigure(kcal, mode).text : kcal);
  const macroNum = (key, grams) => (points ? macroFigure(key, grams, mode).text : grams);
  const slice = (key, grams) => (points ? pointsFromMacro(key, grams) : grams);

  const [clientNutrition, setClientNutrition] = useState({ loading: true, nutrition: {} });
  const [clientWeights, setClientWeights] = useState({ loading: true, data: {} });

  useEffect(() => {
    /*
     * V2 menus, not V1 — this is the default client page, so it is the nutrition most trainers see
     * first. Reading the old tables meant that once trainers moved to the new admin the card showed
     * either nothing or a menu nobody had touched in months.
     */
    listMenus(userId).then(menus => {
      if (menus.length > 0) {
        // A PAUSED menu is not what the trainee is eating. Its numbers still fill the card — a
        // trainer whose only menu is switched off should see what it says — but the card has to
        // report that it is off, or a paused menu reads as a client who is set up and being fed.
        const activeMenu = menus.find(menu => menu.active) || menus[0];
        setClientNutrition({
          loading: false,
          hasMenus: true,
          isActive: !!activeMenu.active,
          nutrition: {
            calories: activeMenu.targets?.calories || 0,
            proteins: activeMenu.targets?.protein || 0,
            carbs: activeMenu.targets?.carbs || 0,
            fats: activeMenu.targets?.fats || 0
          }
        });
      } else {
        setClientNutrition({ loading: false, hasMenus: false, nutrition: {} });
      }
    }).catch(() => setClientNutrition({ loading: false, hasMenus: false, nutrition: {} }));

    getClientWeights(userId).then(r => {
      setClientWeights({ loading: false, data: r.data });
    }).catch(e => {
      notificationHandler.error(getErrorMessage(e.response.data?.errorCode));
    });
  }, []);


  return (
      <>
        {/* First Row: Nutrition and Weight */}
        <Box sx={{
          display: "flex", flexDirection: {xs: "column", md: "row"},
          gap: 2, padding: {xs: "12px 8px", md: "16px"}, paddingBottom: "8px", alignItems: "stretch"
        }}>
          {/* Nutrition Card */}
          <ChartCard
              title="תפריט תזונה" icon={NutritionIcon}
              loading={clientNutrition.loading}
              showData={
                !clientNutrition.loading &&
                Object.keys(clientNutrition.nutrition).length > 0 &&
                Boolean(clientNutrition.isActive)
              }
              emptyMessage={
                clientNutrition.hasMenus
                    ? "אין תפריט פעיל — התפריט הקיים מושהה"
                    : "לא הוגדרו תפריטי תזונה"
              }
              onNavigate={() => navigate(`/app/clients/${userId}/nutrition`)}
              navigationTooltip="עבור לתזונה מלאה"
              flexBasis="50%" contentPadding="20px"
          >
            <Box sx={{display: "flex", flexDirection: {xs: "column", md: "row"}, alignItems: "center", gap: 2, flex: 1}}>
              <Box sx={{flex: 1, width: "100%"}}>
                <Typography sx={{color: colors.textPrimary, fontWeight: 600, fontSize: {xs: "14px", md: "16px"}, mb: 1.5, textAlign: {xs: "center", md: "right"}}}>
                  {energyLabel(mode)}: {energyNum(clientNutrition.nutrition.calories || 0)}
                </Typography>
                <Box sx={{display: "flex", flexDirection: {xs: "row", md: "column"}, justifyContent: {xs: "center", md: "flex-start"}, flexWrap: "wrap", gap: 1.5}}>
                  <NutritionItem label="פחמימה" value={macroNum("carbs", clientNutrition.nutrition.carbs || 0)} color={colors.primary}/>
                  <NutritionItem label="חלבון" value={macroNum("protein", clientNutrition.nutrition.proteins || 0)} color="#69B8AF"/>
                  <NutritionItem label="שומן" value={macroNum("fats", clientNutrition.nutrition.fats || 0)} color="#B9D9D5"/>
                </Box>
              </Box>
              <Box sx={{flex: "0 0 auto", display: "flex", justifyContent: "center"}}>
                <NutritionDonut
                  carbs={slice("carbs", clientNutrition.nutrition.carbs || 0)}
                  proteins={slice("protein", clientNutrition.nutrition.proteins || 0)}
                  fats={slice("fats", clientNutrition.nutrition.fats || 0)}
                  calories={clientNutrition.nutrition.calories || 0}
                  size={200}
                />
              </Box>
            </Box>
          </ChartCard>

          {/* Metrics Card */}
          <ChartCard
              title="מעקב נתונים" icon={WeightIcon}
              loading={clientWeights.loading} showData={!clientWeights.loading}
              emptyMessage="אין נתונים"
              onNavigate={() => navigate(`/app/clients/${userId}/weight`)}
              navigationTooltip="עבור למעקב מלא"
              flexBasis="50%" contentPadding="20px"
          >
            <MetricsChart data={clientWeights.data} userId={userId} />
          </ChartCard>
        </Box>

        {/* Second Row: Body Measurements and Training Blocks */}
        <Box sx={{display: "flex", flexDirection: {xs: "column", md: "row"}, gap: 2, padding: {xs: "12px 8px", md: "16px"}, paddingTop: "8px", alignItems: "stretch"}}>
          <ClientBlocks clientId={userId}/>
          <ClientBodyMeasurements clientId={userId}/>
        </Box>
      </>
  );
};

const NutritionItem = ({label, value, color}) => (
    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
      <Box sx={{width: 12, height: 12, borderRadius: "50%", backgroundColor: color, flexShrink: 0}}/>
      <Typography sx={{fontSize: "14px", color: colors.textSecondary, fontWeight: 500}}>{label}:</Typography>
      <Typography sx={{fontSize: "14px", color: colors.textPrimary, fontWeight: 600}}>{value}</Typography>
    </Box>
);

export default ClientPageMainSection;
